import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from 'components/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import { useDialog } from 'components/Dialog';
import { AddRegionDialogMutation } from '__generated__/AddRegionDialogMutation.graphql';
import { AddRegionDialog_country$key } from '__generated__/AddRegionDialog_country.graphql';
import { useErrorHandler } from 'lib';

type FormFields = {
  name: string;
};

type Props = {
  country: AddRegionDialog_country$key;
  connectionId: string;
};

const initial = {
  name: '',
};

const AddRegionDialog: FC<Props> = ({ connectionId, ...props }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const { id } = useFragment(
    graphql`
      fragment AddRegionDialog_country on Country {
        id
      }
    `,
    props.country
  );

  const [add, adding] = useMutation<AddRegionDialogMutation>(graphql`
    mutation AddRegionDialogMutation(
      $input: CreateRegionInput!
      $connections: [ID!]!
    ) {
      addRegionToCountry(input: $input) {
        node
          @prependNode(connections: $connections, edgeTypeName: "RegionEdge") {
          ...RegionRow_region
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: {
          input: {
            countryId: id,
            ...input,
          },
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [add, close, connectionId, errorHandler, id]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas regionas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują regioną į šalį
          </DialogContentText>

          <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddRegionDialog;
